import React, { useState } from 'react';
import ImageLightbox from './ImageLightbox';
import imgPvcWallPanel from '../assets/our-services/pvc-wall-panel.jpeg';
import imgUvSheetAlmirah from '../assets/our-services/uv-sheet-almirah.jpeg';
import imgModularKitchen from '../assets/our-services/modular-kitchen.png';
import imgVanity from '../assets/our-services/vanity.jpeg';
import imgGrass from '../assets/our-services/grass.png';
import imgLouvers3dUv from '../assets/our-services/louvers-3d-uv-solid-panel.png';

/** Home “Our Services” — fixed list with local assets (`src/assets/our-services/`). Replace files to swap photos. */
const homeServices = [
  {
    id: 'home-svc-pvc',
    title: 'PVC wall panel',
    description:
      'Water-resistant PVC wall panels for a clean, modern finish—easy maintenance and long-lasting appeal for homes and commercial spaces.',
    image: imgPvcWallPanel,
    features: ['Moisture resistant', 'Quick installation', 'Wide finish options'],
  },
  {
    id: 'home-svc-uv-almirah',
    title: 'UV sheet almirah',
    description:
      'UV-coated wardrobe and storage fronts with a high-gloss, scratch-resistant surface—sleek almirah solutions tailored to your layout.',
    image: imgUvSheetAlmirah,
    features: ['UV-coated durability', 'Custom sizes', 'Smooth premium finish'],
  },
  {
    id: 'home-svc-kitchen',
    title: 'Modular kitchen',
    description:
      'End-to-end modular kitchen design with optimized storage, premium hardware, and finishes that match your cooking style.',
    image: imgModularKitchen,
    features: ['Smart storage', 'Modular layouts', 'Hardware & finishes'],
  },
  {
    id: 'home-svc-vanity',
    title: 'Vanity',
    description:
      'Designer bathroom vanities combining storage, mirrors, and durable surfaces for a spa-like, clutter-free wash area.',
    image: imgVanity,
    features: ['Storage-focused design', 'Water-friendly materials', 'Mirror options'],
  },
  {
    id: 'home-svc-grass',
    title: 'Grass',
    description:
      'Grass-texture and designer ceiling / feature treatments for a distinctive look—ideal for accents, ceilings, and feature walls.',
    image: imgGrass,
    features: ['Texture & pattern options', 'Feature walls & ceilings', 'Coordinated with overall theme'],
  },
  {
    id: 'home-svc-louvers',
    title: 'Louvers, 3D UV sheet & solid panel',
    description:
      'Louvers with 3D UV sheet detailing and solid panels—layered wall and partition treatments for depth, privacy, and style.',
    image: imgLouvers3dUv,
    features: ['Louvers & screening', '3D UV sheet accents', 'Solid panel combinations'],
  },
];

const ServicesShowcase = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-8 md:px-16" style={{backgroundColor: '#1A1A1A'}}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-white">
            Our Services
          </h2>
          <p className="text-base sm:text-lg md:text-xl max-w-2xl sm:max-w-3xl mx-auto text-gray-300">
            PVC panels, UV wardrobes, modular kitchens, vanities, grass textures, and louvers with 3D UV & solid panels
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 sm:gap-8">
          {homeServices.map((service) => (
            <div
              key={service.id}
              className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-700 hover:border-gray-600"
            >
              <div
                role="button"
                tabIndex={0}
                className="aspect-video overflow-hidden relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1A1A1A]"
                onClick={() => setLightbox({ src: service.image, alt: service.title })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightbox({ src: service.image, alt: service.title });
                  }
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent z-10 pointer-events-none" />
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none"
                />
              </div>

              <div className="p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-3 text-white">{service.title}</h3>

                <p className="text-sm sm:text-base mb-4 line-clamp-2 text-gray-300">{service.description}</p>

                <div className="space-y-2">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs sm:text-sm text-gray-300">
                      <span className="material-symbols-outlined text-xs sm:text-sm text-amber-400">check_circle</span>
                      <span className="line-clamp-1">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    window.location.href = '/contact?service=' + encodeURIComponent(service.title);
                  }}
                  className="mt-4 w-full px-4 py-2 rounded-full font-medium transition-all duration-300 text-sm sm:text-base hover:scale-105 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg"
                >
                  Inquire Now
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/services"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium transition-all duration-300 text-base sm:text-lg hover:scale-105 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-xl"
          >
            <span>View All Services</span>
            <span className="material-symbols-outlined">arrow_forward</span>
          </a>
        </div>
      </div>

      <ImageLightbox
        isOpen={!!lightbox}
        src={lightbox?.src ?? null}
        alt={lightbox?.alt ?? ''}
        onClose={() => setLightbox(null)}
      />
    </section>
  );
};

export default ServicesShowcase;
